import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { wishlistApi, enrollmentApi } from "../lib";
import { showError, showSuccess } from "../lib/toast";
import {
  ArrowLeft,
  Heart,
  Loader2,
  Trash2,
  XCircle,
  Star,
  Clock,
  Users,
  BookOpen,
  PlayCircle,
  Award,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

interface WishlistCourse {
  _id: string;
  title: string;
  thumbnail: string;
  price: number;
  discount: number;
  slug: string;
  instructorName?: string;
  rating?: number;
  duration?: string;
  students?: number;
  level?: string;
  category?: string;
}

const WishlistPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState<WishlistCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [enrollingId, setEnrollingId] = useState<string | null>(null);

  const fetchWishlist = async (pageNum = 1) => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await wishlistApi.getMyWishlist({
        page: pageNum,
        limit: 12,
      });

      if (response.success) {
        setWishlist(response.data);
        setTotalPages(response.pagination.pages);
        setTotalCount(response.pagination.total);
        setPage(response.pagination.page);
      } else {
        throw new Error("Failed to fetch wishlist");
      }
    } catch (err: any) {
      setError(err.message || "Failed to load wishlist");
      showError(err.message || "Failed to load wishlist");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (courseId: string) => {
    if (!user) return;

    setRemovingId(courseId);
    try {
      const response = await wishlistApi.removeFromWishlist(courseId);
      if (response.success) {
        setWishlist(wishlist.filter((item) => item._id !== courseId));
        setTotalCount(totalCount - 1);
        showSuccess("Removed from wishlist");
      }
    } catch (err: any) {
      showError(err.message || "Failed to remove from wishlist");
    } finally {
      setRemovingId(null);
    }
  };

  const handleEnrollNow = async (courseId: string) => {
    setEnrollingId(courseId);
    try {
      const response = await enrollmentApi.enrollInCourse(courseId);
      if (response.success) {
        showSuccess("Successfully enrolled in course!");
        navigate("/dashboard/student/courses");
      }
    } catch (err: any) {
      showError(err.message || "Failed to enroll");
    } finally {
      setEnrollingId(null);
    }
  };

  useEffect(() => {
    fetchWishlist();
  }, [user, page]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-CM", {
      style: "currency",
      currency: "XAF",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading && wishlist.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-2 text-orange-500 animate-spin" />
          <p className="text-[10px] text-gray-500">Loading wishlist...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="w-full max-w-md p-6 text-center bg-white rounded-xl shadow-sm border border-gray-100">
          <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h2 className="mb-2 text-base font-bold text-gray-900">
            Something went wrong
          </h2>
          <p className="mb-6 text-[11px] text-gray-500">{error}</p>
          <button
            onClick={() => fetchWishlist(page)}
            className="px-4 py-2 text-[11px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (wishlist.length === 0 && !loading) {
    return (
      <div className="min-h-screen py-12 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="px-4 mx-auto text-center max-w-7xl">
          <div className="p-6 bg-white border border-gray-100 rounded-xl shadow-sm md:p-8">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
              <Heart className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="mb-2 text-base font-bold text-gray-900">
              Your wishlist is empty
            </h2>
            <p className="mb-6 text-[10px] text-gray-500 max-w-xs mx-auto">
              Add courses to your wishlist to save them for later enrollment
            </p>
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
            >
              <BookOpen className="w-3.5 h-3.5" />
              Browse Courses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="flex items-center gap-1.5">
                <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500">
                  <Heart className="w-3.5 h-3.5 text-white" />
                </div>
                <h1 className="text-sm font-bold text-gray-900">My Wishlist</h1>
              </div>
              <p className="text-[10px] text-gray-500 ml-8 mt-0.5">
                {totalCount} {totalCount === 1 ? "course" : "courses"} saved
              </p>
            </div>
          </div>
        </div>

        {/* Wishlist Grid - Responsive */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {wishlist.map((course) => (
            <div
              key={course._id}
              className="flex flex-col overflow-hidden transition-all duration-300 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md"
            >
              {/* Course Thumbnail */}
              <div className="relative h-40 overflow-hidden bg-gray-100 sm:h-44 md:h-48">
                <img
                  src={
                    course.thumbnail ||
                    "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&h=300&fit=crop"
                  }
                  alt={course.title}
                  className="object-cover w-full h-full transition-transform duration-300 hover:scale-105"
                />

                {/* Discount Badge */}
                {course.discount > 0 && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                    -{course.discount}%
                  </div>
                )}

                {/* Level Badge */}
                {course.level && (
                  <div className="absolute bottom-2 left-2">
                    <span className="px-1.5 py-0.5 text-[8px] font-medium text-white bg-black/70 rounded">
                      {course.level}
                    </span>
                  </div>
                )}

                {/* Remove Button */}
                <button
                  onClick={() => handleRemoveFromWishlist(course._id)}
                  disabled={removingId === course._id}
                  className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                >
                  {removingId === course._id ? (
                    <Loader2 className="w-3.5 h-3.5 text-red-500 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5 text-gray-500 hover:text-red-500" />
                  )}
                </button>
              </div>

              {/* Course Info */}
              <div className="flex flex-col flex-1 p-3 md:p-4">
                {/* Category Badge */}
                {course.category && (
                  <div className="mb-1.5">
                    <span className="text-[9px] font-medium text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full">
                      {course.category}
                    </span>
                  </div>
                )}

                {/* Rating */}
                <div className="flex items-center gap-1 mb-1">
                  <div className="flex items-center gap-0.5">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    <span className="text-[10px] font-medium text-gray-700">
                      {course.rating?.toFixed(1) || "4.8"}
                    </span>
                  </div>
                  <span className="text-[8px] text-gray-400">
                    ({course.rating ? Math.round(course.rating * 100) : 1200}+)
                  </span>
                </div>

                {/* Title */}
                <Link to={`/course/${course._id}`}>
                  <h3 className="mb-1 text-[11px] font-semibold text-gray-900 transition-colors line-clamp-2 hover:text-orange-600 md:text-xs">
                    {course.title}
                  </h3>
                </Link>

                {/* Instructor */}
                {course.instructorName && (
                  <p className="mb-1.5 text-[9px] text-gray-500 truncate">
                    by {course.instructorName}
                  </p>
                )}

                {/* Meta Info */}
                <div className="flex items-center gap-2 mb-2 text-[9px] text-gray-500">
                  <div className="flex items-center gap-0.5">
                    <Clock className="w-2.5 h-2.5" />
                    <span>{course.duration || "20h"}</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Users className="w-2.5 h-2.5" />
                    <span>{course.students || 1200}</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Award className="w-2.5 h-2.5" />
                    <span>Cert</span>
                  </div>
                </div>

                {/* Price & Actions */}
                <div className="pt-2 mt-auto border-t border-gray-100">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-baseline gap-1 flex-wrap">
                        <span className="text-sm font-bold text-orange-600">
                          {formatCurrency(course.price)}
                        </span>
                        {course.discount > 0 && (
                          <span className="text-[8px] text-gray-400 line-through">
                            {formatCurrency(
                              course.price / (1 - course.discount / 100),
                            )}
                          </span>
                        )}
                      </div>
                      {course.discount > 0 && (
                        <span className="text-[8px] font-bold text-red-500">
                          Save {course.discount}%
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => handleEnrollNow(course._id)}
                      disabled={enrollingId === course._id}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[9px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all whitespace-nowrap"
                    >
                      {enrollingId === course._id ? (
                        <Loader2 className="w-2.5 h-2.5 animate-spin" />
                      ) : (
                        <PlayCircle className="w-2.5 h-2.5" />
                      )}
                      Enroll
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination - Mobile responsive */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center gap-3 mt-8 sm:flex-row sm:justify-center">
            <button
              onClick={() => {
                const newPage = Math.max(1, page - 1);
                setPage(newPage);
                fetchWishlist(newPage);
              }}
              disabled={page === 1}
              className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => {
                      setPage(pageNum);
                      fetchWishlist(pageNum);
                    }}
                    className={`w-7 h-7 text-[10px] font-medium rounded-lg transition-colors ${
                      page === pageNum
                        ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white"
                        : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => {
                const newPage = Math.min(totalPages, page + 1);
                setPage(newPage);
                fetchWishlist(newPage);
              }}
              disabled={page === totalPages}
              className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Stats Summary Footer */}
        {wishlist.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <Heart className="w-4 h-4 mx-auto mb-0.5 text-red-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {totalCount}
              </p>
              <p className="text-[7px] text-gray-500">Items</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <Star className="w-4 h-4 mx-auto mb-0.5 text-yellow-400 fill-yellow-400" />
              <p className="text-[10px] font-bold text-gray-900">
                {wishlist.filter((c) => (c.rating || 4.8) >= 4.5).length}
              </p>
              <p className="text-[7px] text-gray-500">Top Rated</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <TrendingUp className="w-4 h-4 mx-auto mb-0.5 text-green-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {Math.min(...wishlist.map((c) => c.price))?.toLocaleString() ||
                  0}
              </p>
              <p className="text-[7px] text-gray-500">Min Price</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
